import { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footer from "components/footers/Footer.jsx";
import { useTranslation } from 'react-i18next';
import NotificationBell from 'components/NotificationBell';
import { sendToGoodDelivery, getOrderUniqueId } from './deliveryHelper';
import Swal from 'sweetalert2';

const OrderLpAdmin = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [deliveryLoadingId, setDeliveryLoadingId] = useState(null);

    useEffect(() => {
        fetchLpOrders();
    }, [currentPage]);

    const fetchLpOrders = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`https://rehabwmg.online/api/admin/orders-lp?page=${currentPage}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                const data = response.data.data || response.data;
                const fetchedOrders = data.data || data;
                
                setOrders([...fetchedOrders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
                setTotalPages(response.data.last_page || 1);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des commandes LP", error);
        }
    };

    const handleSendToDelivery = async (order) => {
        // 1. Formater l'objet pour qu'il corresponde à la structure attendue par deliveryHelper
        const formattedOrder = {
            ...order,
            OrderID: order.custom_order_id || order.id,
            customer_info: {
                Name: order.client_name,
                Phone: order.phone,
                city: order.city,
                Address: order.address || order.city
            },
            order_items: [
                {
                    product: {
                        Name: {
                            fr: order.product_name,
                            ar: order.product_name
                        }
                    },
                    Quantity: order.quantity || 1,
                    Price: order.total_price
                }
            ],
            payment: {
                Amount: order.total_price,
                PaymentStatus: "Paid"
            }
        };

        const orderId = getOrderUniqueId(formattedOrder);
        
        // 2. Afficher immédiatement l'alerte de chargement (Message d'attente)
        Swal.fire({
            title: t('Envoi en cours...'),
            text: t('Veuillez patienter pendant l\'export vers GoodDelivery.'),
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading(); // Active le spinner de chargement de SweetAlert2
            }
        });

        try {
            // 3. Exécuter l'envoi vers la passerelle de livraison avec l'objet converti
            await sendToGoodDelivery(
                formattedOrder,  
                t,  
                (loadingState) => {
                    if (loadingState) setDeliveryLoadingId(orderId);
                    else setDeliveryLoadingId(null);
                }
            );

            // Fermer le loader d'attente une fois l'envoi réussi
            Swal.close();

            // Note : Décommentez ci-dessous si sendToGoodDelivery ne gère pas son propre message de succès :
            /*
            Swal.fire({
                icon: 'success',
                title: t('Succès !'),
                text: t('La commande a été envoyée avec succès.'),
                timer: 2000,
                showConfirmButton: false
            });
            */

        } catch (error) {
            console.error("Erreur lors de l'export GoodDelivery:", error);
            
            // 4. Afficher un message d'erreur si la requête API échoue
            Swal.fire({
                icon: 'error',
                title: t('Échec de l\'envoi'),
                text: error.message || t('Une erreur est survenue lors de l\'envoi.'),
                confirmButtonColor: '#6B1615'
            });
        }
    };

    const handleStatusChange = async (orderID, newStatus) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            
            const response = await axios.put(
                `https://rehabwmg.online/api/admin/orders-lp/${orderID}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200) {
                Swal.fire({
                    icon: 'success',
                    title: t('Statut mis à jour'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
                fetchLpOrders();
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour du statut:", error);
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: error.response?.data?.error || error.message,
            });
        }
    };

    const filteredOrders = orders.filter(order =>
        order.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.custom_order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.phone?.includes(searchTerm) ||
        order.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <AnimationRevealPage>
            <LightAdmin />
            <NotificationBell />
            <div className="w-100 p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h1 className="text-dark display-5 m-0 fw-bold">{t('Commandes Landing Page')}</h1>
                    <input
                        type="text"
                        className="form-control border-dark shadow-sm"
                        placeholder={t('Rechercher par nom, ID ou tél')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ maxWidth: '300px', borderRadius: '20px' }}
                    />
                </div>
                <hr className="my-4" />

                <div className="table-responsive shadow-sm rounded">
                    <table className="table table-striped table-hover align-middle">
                        <thead className="table-primary">
                            <tr>
                                <th>{t('ID Commande')}</th>
                                <th>{t('Nom Client')}</th>
                                <th>{t('Téléphone')}</th>
                                <th>{t('Ville')}</th>
                                <th>{t('Addresse')}</th>
                                <th>{t('Produit')}</th>
                                <th>{t('Quantité')}</th>
                                <th>{t('Prix Total')}</th>
                                <th>{t('Statut')}</th>
                                <th className="text-center">{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.length > 0 ? (
                                filteredOrders.map((order) => (
                                    <tr key={order.id}>
                                        <td><strong>{order.custom_order_id}</strong></td>
                                        <td>{order.client_name}</td>
                                        <td>
                                            <a href={`tel:${order.phone}`} style={{ color: 'blue', textDecoration: 'underline' }}>
                                                {order.phone}
                                            </a>
                                        </td>
                                        <td>{order.city}</td>
                                        <td>{order.address}</td>
                                        <td>{order.product_name}</td>
                                        <td>x{order.quantity}</td>
                                        <td>{order.total_price} DH</td>
                                        <td>
                                            <select
                                                value={order.status}
                                                onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                                className="form-select form-select-sm"
                                                style={{ width: '140px' }}
                                                disabled={['livré', 'annulé'].includes(order.status)}
                                            >
                                                <option value="en_attente">{t('En attente')}</option>
                                                <option value="confirmé">{t('Confirmé')}</option>
                                                <option value="expédié">{t('Expédié')}</option>
                                                <option value="livré">{t('Livré')}</option>
                                                <option value="annulé">{t('Annulé')}</option>
                                            </select>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-center">
                                                <button
                                                    className="btn btn-sm btn-success"
                                                    onClick={() => handleSendToDelivery(order)}
                                                    disabled={deliveryLoadingId === getOrderUniqueId(order)}
                                                    title={t('Envoyer à GoodDelivery')}
                                                >
                                                    {deliveryLoadingId === getOrderUniqueId(order) ? (
                                                        <i className="fas fa-spinner fa-spin"></i>
                                                    ) : (
                                                        <i className="fas fa-shipping-fast"></i>
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center text-muted py-4">{t('Aucune commande trouvée')}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <nav>
                            <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                        {t('Previous')}
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, idx) => (
                                    <li key={idx} className={`page-item ${currentPage === idx + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(idx + 1)}>
                                            {idx + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                        {t('Next')}
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>
            <Footer />
        </AnimationRevealPage>
    );
};

export default OrderLpAdmin;